<template>
  <div>
    <v-card flat>
      <v-card-title class="d-flex align-center pr-0 pl-0">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('search')"
          single-line
          hide-details
          class="max-width-300 me-4"
          @input="loadTickets"
        ></v-text-field>

        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          item-text="text"
          item-value="value"
          :label="$t('Status')"
          hide-details
          class="max-width-200"
          @change="loadTickets"
        ></v-select>
      </v-card-title>

      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="headers"
        :items="tickets"
        item-key="id"
        class="elevation-0 mt-4"
      >
        <!-- Driver Info Column -->
        <template v-slot:item.driver.name="{ item }">
          <div class="d-flex align-center" v-if="item.driver">
            <v-avatar size="32" class="me-2">
              <v-img :src="getAvatar(item.driver.avatar)"></v-img>
            </v-avatar>
            <div>
              <div class="font-weight-bold">{{ item.driver.name }}</div>
              <small class="text-muted">{{ item.driver.email }}</small>
            </div>
          </div>
          <span v-else class="text-muted">-</span>
        </template>

        <!-- Status Column -->
        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" dark small>
            {{ getStatusLabel(item.status) }}
          </v-chip>
        </template>

        <!-- Date Column -->
        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>

        <!-- Description Column -->
        <template v-slot:item.description="{ item }">
          <span class="text-truncate d-inline-block" style="max-width: 250px;">{{ item.description }}</span>
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
          <v-btn icon color="primary" small @click="viewDetails(item)" :title="$t('Details')">
            <v-icon>mdi-eye</v-icon>
          </v-btn>
          <v-btn icon color="success" small @click="openReplyDialog(item)" :title="$t('reply_to_report')">
            <v-icon>mdi-reply</v-icon>
          </v-btn>
          <v-btn
            v-if="item.status !== 'closed'"
            icon
            color="warning"
            small
            @click="confirmCloseTicket(item)"
            title="إغلاق البلاغ"
          >
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
          <v-btn icon color="error" small @click="confirmDelete(item)" :title="$t('Delete')">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="600px">
      <v-card v-if="selectedTicket">
        <v-card-title class="headline primary class white--text">
          {{ $t('driver_reports') }} #{{ selectedTicket.id }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" sm="6">
              <div class="subtitle-2 text-muted">{{ $t('driver_name') }}</div>
              <div class="body-1 font-weight-bold" v-if="selectedTicket.driver">{{ selectedTicket.driver.name }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="subtitle-2 text-muted">{{ $t('driver_phone') }}</div>
              <div class="body-1" v-if="selectedTicket.driver">{{ selectedTicket.driver.tel_number || '-' }}</div>
            </v-col>
            <v-col cols="12">
              <div class="subtitle-2 text-muted">{{ $t('driver_email') }}</div>
              <div class="body-1" v-if="selectedTicket.driver">{{ selectedTicket.driver.email }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="subtitle-2 text-muted">{{ $t('report_category') }}</div>
              <div class="body-1 font-weight-bold">{{ selectedTicket.category || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="subtitle-2 text-muted">{{ $t('Status') }}</div>
              <v-chip :color="getStatusColor(selectedTicket.status)" dark small class="mt-1">
                {{ getStatusLabel(selectedTicket.status) }}
              </v-chip>
            </v-col>
            <v-col cols="12">
              <div class="subtitle-2 text-muted mb-1 font-weight-bold">{{ $t('report_description') }}</div>
            </v-col>

            <!-- Conversation Messages Thread -->
            <v-col cols="12">
              <div v-if="selectedTicket.messages && selectedTicket.messages.length > 0" class="conversation-container p-3 rounded" style="background-color: #fafafa; border: 1px solid #e0e0e0; max-height: 350px; overflow-y: auto;">
                <div 
                  v-for="(msg, index) in selectedTicket.messages" 
                  :key="msg.id || index"
                  class="mb-3"
                >
                  <div class="d-flex align-center mb-1">
                    <v-avatar size="24" class="me-2" :color="msg.sender_type === 'admin' ? 'success' : 'primary'">
                      <span class="white--text caption font-weight-bold">{{ msg.sender_type === 'admin' ? 'أ' : 'س' }}</span>
                    </v-avatar>
                    <span class="caption font-weight-bold me-2" :class="msg.sender_type === 'admin' ? 'success--text' : 'primary--text'">
                      {{ msg.sender_type === 'admin' ? (msg.sender ? msg.sender.name : 'الإدارة') : (selectedTicket.driver ? selectedTicket.driver.name : 'السائق') }}
                    </span>
                    <small class="text-muted ms-auto" v-if="msg.created_at">{{ msg.created_at | moment("LLL") }}</small>
                  </div>

                  <div 
                    class="body-2 p-3 rounded" 
                    :style="msg.sender_type === 'admin' 
                      ? 'background-color: #e8f5e9; border-left: 4px solid #4caf50; padding: 10px; margin-right: 15px;' 
                      : 'background-color: #e3f2fd; border-left: 4px solid #2196f3; padding: 10px; margin-right: 15px;'"
                    style="white-space: pre-line;"
                  >
                    {{ msg.message }}
                  </div>
                </div>
              </div>

              <!-- Legacy Fallback -->
              <div v-else>
                <div class="body-1 bg-light p-3 rounded" style="white-space: pre-line; background-color: #f5f5f5; padding: 10px;">{{ selectedTicket.description }}</div>
                <div v-if="selectedTicket.admin_reply" class="body-1 bg-light p-3 rounded mt-2 border-success" style="white-space: pre-line; background-color: #e8f5e9; padding: 10px; border-left: 4px solid #4caf50;">
                  <div class="font-weight-bold success--text mb-1">{{ $t('admin_reply') }}</div>
                  {{ selectedTicket.admin_reply }}
                  <div class="text-right mt-2" v-if="selectedTicket.replied_at">
                    <small class="text-muted">{{ selectedTicket.replied_at | moment("LLL") }}</small>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn
            v-if="selectedTicket.status !== 'closed'"
            color="error"
            depressed
            @click="confirmCloseTicket(selectedTicket)"
          >
            <v-icon left>mdi-close-circle-outline</v-icon>
            إغلاق البلاغ
          </v-btn>
          <v-btn
            v-if="selectedTicket.status !== 'closed'"
            color="success"
            depressed
            class="ms-2"
            @click="openReplyDialog(selectedTicket)"
          >
            <v-icon left>mdi-reply</v-icon>
            إضافة رد
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailsDialog = false">{{ $t('Back') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reply Dialog -->
    <v-dialog v-model="replyDialog" max-width="500px">
      <v-card v-if="selectedTicket">
        <v-card-title class="headline success white--text">
          {{ $t('reply_to_report') }} #{{ selectedTicket.id }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="replyForm" v-model="isFormValid">
            <v-textarea
              v-model="replyText"
              :label="$t('admin_reply')"
              rows="5"
              outlined
              required
              :rules="[v => !!v || 'Reply content is required']"
            ></v-textarea>

            <v-select
              v-model="replyStatus"
              :items="replyStatusOptions"
              item-text="text"
              item-value="value"
              :label="$t('Status')"
              outlined
            ></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions class="pb-4 pr-4 pl-4">
          <v-btn color="error" text @click="replyDialog = false">{{ $t('cancel') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="success" :disabled="!isFormValid || isSubmitting" :loading="isSubmitting" @click="submitReply">
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "DriverReportsTab",
  data() {
    return {
      tickets: [],
      isLoading: false,
      search: "",
      statusFilter: "all",
      detailsDialog: false,
      replyDialog: false,
      selectedTicket: null,
      replyText: "",
      replyStatus: "replied",
      isFormValid: false,
      isSubmitting: false,
      statusOptions: [
        { text: this.$t('working_status_all') || 'الكل', value: "all" },
        { text: this.$t('new') || 'جديدة', value: "new" },
        { text: this.$t('in_review') || 'قيد المراجعة', value: "in_review" },
        { text: this.$t('replied') || 'تم الرد', value: "replied" },
        { text: this.$t('closed') || 'مغلقة', value: "closed" },
      ],
      replyStatusOptions: [
        { text: this.$t('in_review') || 'قيد المراجعة', value: "in_review" },
        { text: this.$t('replied') || 'تم الرد', value: "replied" },
      ],
      headers: [
        { text: this.$t('report_number') || 'رقم البلاغ', value: "id", align: "start" },
        { text: this.$t('driver_name') || 'اسم السائق', value: "driver.name" },
        { text: this.$t('report_category') || 'نوع البلاغ', value: "category" },
        { text: this.$t('report_description') || 'الوصف', value: "description" },
        { text: this.$t('Status') || 'الحالة', value: "status" },
        { text: this.$t('created_at') || 'تاريخ الإرسال', value: "created_at" },
        { text: this.$t('Actions') || 'الإجراءات', value: "actions", sortable: false, align: "center" },
      ]
    };
  },
  mounted() {
    this.loadTickets();
  },
  methods: {
    loadTickets() {
      this.isLoading = true;
      const params = {
        ticket_type: "report",
        search: this.search
      };
      if (this.statusFilter && this.statusFilter !== 'all') {
        params.status = this.statusFilter;
      }
      axios
        .get("/driver-support-tickets", {
          params: params
        })
        .then(response => {
          this.tickets = response.data;
        })
        .catch(error => {
          console.error(error);
          this.$notify({
            title: "Error",
            text: "Failed to load driver reports",
            type: "error"
          });
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    getAvatar(avatar) {
      if (avatar && avatar !== 'avatar.png') {
        return avatar;
      }
      return '/avatar.png';
    },
    getStatusColor(status) {
      switch (status) {
        case "new":
          return "orange";
        case "in_review":
          return "blue";
        case "replied":
          return "green";
        case "closed":
          return "grey";
        default:
          return "primary";
      }
    },
    getStatusLabel(status) {
      switch (status) {
        case "new":
          return this.$t('new') || "جديدة";
        case "in_review":
          return this.$t('in_review') || "قيد المراجعة";
        case "replied":
          return this.$t('replied') || "تم الرد";
        case "closed":
          return this.$t('closed') || "مغلقة";
        default:
          return status;
      }
    },
    viewDetails(ticket) {
      this.selectedTicket = ticket;
      this.detailsDialog = true;
    },
    openReplyDialog(ticket) {
      this.selectedTicket = ticket;
      this.replyText = ticket.admin_reply || "";
      this.replyStatus = ticket.status === "new" ? "replied" : ticket.status;
      this.replyDialog = true;
    },
    submitReply() {
      if (!this.$refs.replyForm.validate()) return;
      this.isSubmitting = true;
      axios
        .post(`/driver-support-tickets/${this.selectedTicket.id}/reply`, {
          admin_reply: this.replyText,
          status: this.replyStatus
        })
        .then(response => {
          this.$notify({
            title: "نجاح",
            text: "تم إرسال الرد بنجاح.",
            type: "success"
          });
          if (response.data && response.data.data) {
            this.selectedTicket = response.data.data;
          }
          this.replyText = "";
          this.replyDialog = false;
          this.loadTickets();
        })
        .catch(error => {
          console.error(error);
          this.$notify({
            title: "خطأ",
            text: "حدث خطأ أثناء إرسال الرد",
            type: "error"
          });
        })
        .then(() => {
          this.isSubmitting = false;
        });
    },
    confirmDelete(ticket) {
      this.$swal({
        title: this.$t('confirm_delete') || "Are you sure?",
        text: "You will not be able to recover this ticket!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t('yes_delete') || "Yes, delete it!",
        cancelButtonText: this.$t('cancel') || "Cancel"
      }).then(result => {
        if (result.isConfirmed) {
          this.deleteTicket(ticket.id);
        }
      });
    },
    confirmCloseTicket(ticket) {
      this.$swal({
        title: "إغلاق البلاغ",
        text: `هل أنت تأكد من إغلاق البلاغ رقم #${ticket.id}؟`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "نعم، إغلاق البلاغ",
        cancelButtonText: "إلغاء"
      }).then(result => {
        if (result.isConfirmed) {
          this.closeTicket(ticket.id);
        }
      });
    },
    closeTicket(id) {
      this.isLoading = true;
      axios
        .post(`/driver-support-tickets/${id}/close`)
        .then(response => {
          this.$notify({
            title: "نجاح",
            text: "تم إغلاق البلاغ بنجاح.",
            type: "success"
          });
          this.detailsDialog = false;
          this.loadTickets();
        })
        .catch(error => {
          console.error(error);
          this.$notify({
            title: "خطأ",
            text: "حدث خطأ أثناء إغلاق البلاغ",
            type: "error"
          });
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    deleteTicket(id) {
      this.isLoading = true;
      axios
        .delete(`/driver-support-tickets/${id}`)
        .then(() => {
          this.$notify({
            title: "Success",
            text: "Ticket deleted successfully",
            type: "success"
          });
          this.loadTickets();
        })
        .catch(error => {
          console.error(error);
          this.$notify({
            title: "Error",
            text: "Failed to delete ticket",
            type: "error"
          });
        })
        .then(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>

<style scoped>
.max-width-300 {
  max-width: 300px;
}
.max-width-200 {
  max-width: 200px;
}
</style>
